/**
 * Sipfull horse-portrait product section — behaviour IIFE.
 *
 * Variant picker, price + stock updates, hero/thumb swaps, quantity
 * stepper, "You're ordering" summary, peek scroll dance, and the
 * ATC gating logic that respects data-aa-blocked (horse portrait
 * intake) alongside data-ra-blocked (rolling art intake).
 *
 * Extracted from sections/sipfull-product-horse-portrait.liquid on
 * 2026-08-18 to keep that section file under Shopify's 256 KB limit.
 * The IIFE had one Liquid interpolation ({{ section.id }} in the root
 * querySelector) — dropped here because horse-portrait PDPs only ever
 * render one instance of this section, so matching on
 * [data-sipfull-product] alone is safe. Same trick used for
 * assets/sipfull-product-personalised.js.
 *
 * Loaded via <script src=".." defer> in the section markup so
 * behaviour is identical to when it was inline.
 */
  (function () {
    var root = document.querySelector('[data-sipfull-product]');
    if (!root) return;

    var select = root.querySelector('[data-sp-variant-select]');
    var priceWrap = root.querySelector('[data-sp-price-wrap]');
    var atcBtn = root.querySelector('[data-sp-atc]');
    var atcTxt = root.querySelector('[data-sp-atc-text]');
    var heroImg = root.querySelector('[data-sp-hero]');
    var thumbs = root.querySelectorAll('[data-sp-thumb]');
    var qtyInput = root.querySelector('[data-sp-qty-input]');
    var qtyDec = root.querySelector('[data-sp-qty-dec]');
    var qtyInc = root.querySelector('[data-sp-qty-inc]');
    /* Selection summary + stock banner refs — added 2026-07-05 with
       the "You're ordering" + "In Stock" port from non-personalised.
       Used from within updateForVariantId() to keep both in sync
       with the currently-selected variant. */
    var selectionValue = root.querySelector('[data-sp-selection-value]');
    var stockBanner = root.querySelector('[data-sp-stock-banner]');
    var stockBannerText = root.querySelector('[data-sp-stock-banner-text]');

    /* --- Variant data ------------------------------------------------ */
    var variants = [];
    var dataEl = root.querySelector('script[type="application/json"][data-sp-variants]');
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = []; }
    }

    /* --- Peek-to-top-and-return -------------------------------------
       Shared helper for variant-thumb + picker-pill click handlers.
       Ported from sipfull-product.liquid on 2026-07-05.

       Behaviour: on mobile, smooth-scroll to page top so the customer
       SEES the mug in the new variant. Dwell briefly, then smooth-
       scroll back down to where they were configuring.

       Burst-tap: if the customer taps ANOTHER variant during the
       dwell, we keep them at top and reset the return timer. When
       the burst ends, we return to the ORIGINAL position from
       before the first tap — so rapid A/B toggling doesn't lose
       their configuring context.

       Cancellation: if the customer manually scrolls (touchmove or
       wheel) during the dwell or return, drop the return.

       Not fired on desktop — the layout keeps hero + picker visible
       together, no scroll needed. */
    var peekReturnTimer = null;
    var peekReturnY = null;
    var peekCancelled = false;
    var DWELL_MS = 1400;

    function peekToTop() {
      var isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) return;

      /* Rolling Art has a full-width hero (interactive slider +
         icon workflow) that sits ABOVE the standard gallery. Peeking
         to page top on every variant tap now scrolls the customer
         way past the mug preview all the way to the hero — jarring,
         and unhelpful because the hero isn't what changes on a
         variant swap. Bail early if the hero is on the page. Other
         personalised products (photo mugs, numberplates etc.) don't
         render the hero, so they keep the peek behaviour. */
      if (document.querySelector('.ra-hero')) return;

      if (peekReturnY === null) peekReturnY = window.scrollY;
      peekCancelled = false;

      if (peekReturnTimer) {
        clearTimeout(peekReturnTimer);
        peekReturnTimer = null;
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });

      peekReturnTimer = setTimeout(function () {
        var target = peekReturnY;
        peekReturnY = null;
        peekReturnTimer = null;
        if (target !== null && !peekCancelled && target > 0) {
          window.scrollTo({ top: target, behavior: 'smooth' });
        }
      }, DWELL_MS);
    }

    function cancelPeek() {
      peekCancelled = true;
      peekReturnY = null;
      if (peekReturnTimer) {
        clearTimeout(peekReturnTimer);
        peekReturnTimer = null;
      }
    }
    window.addEventListener('touchmove', cancelPeek, { passive: true });
    window.addEventListener('wheel', cancelPeek, { passive: true });

    function formatMoney(cents) {
      return '£' + (cents / 100).toFixed(2);
    }

    function updateForVariantId(id) {
      var v = variants.find(function (x) { return String(x.id) === String(id); });
      if (!v) return;

      /* Rewrap the price block rather than mutating individual elements —
         simpler than juggling class toggles for sale vs regular. */
      if (priceWrap) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          priceWrap.innerHTML =
            '<span class="sp-price-sale" data-sp-price>' + formatMoney(v.price) + '</span>' +
            '<span class="sp-price-compare" data-sp-compare>' + formatMoney(v.compare_at_price) + '</span>';
        } else {
          priceWrap.innerHTML =
            '<span class="sp-price-regular" data-sp-price>' + formatMoney(v.price) + '</span>';
        }
      }

      if (atcBtn && atcTxt) {
        if (v.available) {
          /* Two intakes may gate this button:
               • Rolling Art (data-ra-blocked) — cleared when photo +
                 car description are filled.
               • Auto-Art / Horse Portrait (data-aa-blocked) — cleared
                 when the customer has uploaded a photo AND picked a
                 generated portrait.
             Respect whichever is set — otherwise this handler runs on
             every variant change (including page load) and re-enables
             a button the intake wants kept disabled. Also preserve
             whatever "please X first" label the intake is showing;
             only revert to "Add to cart" when nothing is blocking. */
          var blocked = atcBtn.dataset.raBlocked === '1' || atcBtn.dataset.aaBlocked === '1';
          if (blocked) {
            atcBtn.setAttribute('disabled', 'disabled');
          } else {
            atcBtn.removeAttribute('disabled');
            atcTxt.textContent = 'Add to cart';
          }
        } else {
          atcBtn.setAttribute('disabled', 'disabled');
          atcTxt.textContent = 'Sold out';
        }
      }

      /* "You're ordering: 11oz · Ceramic · Blue" — reversed options
         to match the non-personalised template's reading order (size
         first, then material, then colour). */
      if (selectionValue && v.options) {
        selectionValue.textContent = v.options.slice().reverse().join(' · ');
      }

      /* Stock reassurance banner — palette + copy swap depending on
         v.available. Same logic as the non-personalised handler. */
      if (stockBanner && stockBannerText) {
        if (v.available) {
          stockBanner.classList.remove('sp-stock-banner--sold-out');
          stockBanner.classList.add('sp-stock-banner--in-stock');
          stockBannerText.textContent = 'In Stock · Ready to Press';
        } else {
          stockBanner.classList.remove('sp-stock-banner--in-stock');
          stockBanner.classList.add('sp-stock-banner--sold-out');
          stockBannerText.textContent = 'Currently sold out';
        }
      }

      /* Swap hero image to the variant's featured image if it has one. */
      if (heroImg && v.featured_image && v.featured_image.src) {
        heroImg.src = v.featured_image.src;
        heroImg.removeAttribute('srcset');
      }

      /* Reflect selected variant in URL for deep-link / share. */
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* noop */ }
    }

    if (select) {
      select.addEventListener('change', function (e) {
        updateForVariantId(e.target.value);
      });

      /* Sync price to whatever the select actually resolved to on load.
         This covers two cases:
           1. A ?variant= deep-link (use that id)
           2. No deep-link, but current_variant (Liquid) may have chosen a
              different option than the browser's default selected — force
              them into agreement by reading select.value and updating. */
      var params = new URLSearchParams(window.location.search);
      var deepLink = params.get('variant');
      if (deepLink) {
        select.value = deepLink;
        updateForVariantId(deepLink);
      } else if (select.value) {
        updateForVariantId(select.value);
      }
    }

    /* --- Thumbnail swap (image + video) ----------------------------- */
    /* Image thumb: swap hero <img> src/srcset, hide video if visible.
       Video thumb: hide hero <img>, set <video> src + load + play.
       Video element is only in the DOM when the product has >=1 video
       (guarded by has_video in Liquid), so heroVideo may be null.
       Prev/Next arrows step through the thumb list with wrap-around
       by triggering a .click() on the sibling thumb — reuses all the
       thumb logic below without duplicating it. */
    var heroVideo = root.querySelector('[data-sp-hero-video]');
    var heroPrev = root.querySelector('[data-sp-hero-prev]');
    var heroNext = root.querySelector('[data-sp-hero-next]');

    function stepHero(direction) {
      if (!thumbs.length) return;
      var activeIndex = 0;
      for (var i = 0; i < thumbs.length; i++) {
        if (thumbs[i].classList.contains('is-active')) { activeIndex = i; break; }
      }
      var nextIndex = (activeIndex + direction + thumbs.length) % thumbs.length;
      thumbs[nextIndex].click();
      /* Scroll the thumb row so the newly active thumb stays in view. */
      thumbs[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    if (heroPrev) heroPrev.addEventListener('click', function () { stepHero(-1); });
    if (heroNext) heroNext.addEventListener('click', function () { stepHero(1); });

    /* --- Thumb-strip pagination arrows ----------------------------
       Separate from the hero nav. These scroll the thumb strip
       horizontally without changing the hero image — used when there
       are >4 media items and the strip overflows its 4-thumb width
       on desktop. Each click advances by one thumb-width + gap.
       Disabled state mirrors scroll position so the user knows when
       they've reached either end of the strip. */
    var thumbsContainer = root.querySelector('[data-sp-thumbs]');
    var thumbsPrev = root.querySelector('[data-sp-thumbs-prev]');
    var thumbsNext = root.querySelector('[data-sp-thumbs-next]');

    function updateThumbsNavState() {
      if (!thumbsContainer) return;
      var atStart = thumbsContainer.scrollLeft <= 0;
      var atEnd = thumbsContainer.scrollLeft + thumbsContainer.clientWidth
                  >= thumbsContainer.scrollWidth - 1;
      if (thumbsPrev) thumbsPrev.disabled = atStart;
      if (thumbsNext) thumbsNext.disabled = atEnd;
    }

    function scrollThumbs(direction) {
      if (!thumbsContainer) return;
      var firstThumb = thumbsContainer.querySelector('.sp-thumb');
      var step = firstThumb ? (firstThumb.offsetWidth + 8) : 88;
      thumbsContainer.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    if (thumbsPrev) thumbsPrev.addEventListener('click', function () { scrollThumbs(-1); });
    if (thumbsNext) thumbsNext.addEventListener('click', function () { scrollThumbs(1); });
    if (thumbsContainer) {
      thumbsContainer.addEventListener('scroll', updateThumbsNavState);
      /* Set the initial disabled state on load — prev starts disabled
         because scrollLeft is 0. Also catches the case where the
         browser rounds scrollWidth vs clientWidth so no actual scroll
         is possible (e.g. in test states where only 5 thumbs render). */
      updateThumbsNavState();
    }

    if (thumbs.length && heroImg) {
      thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var type = btn.getAttribute('data-media-type') || 'image';

          if (type === 'video' && heroVideo) {
            var vSrc = btn.getAttribute('data-video-src');
            var vType = btn.getAttribute('data-video-type') || 'video/mp4';
            if (vSrc && heroVideo.getAttribute('src') !== vSrc) {
              heroVideo.setAttribute('src', vSrc);
              heroVideo.setAttribute('type', vType);
              heroVideo.load();
            }
            heroImg.hidden = true;
            heroVideo.hidden = false;
            var playPromise = heroVideo.play();
            if (playPromise && playPromise.catch) {
              /* Autoplay may be blocked — browser will show controls. */
              playPromise.catch(function () {});
            }
          } else {
            var src = btn.getAttribute('data-hero-src');
            var srcset = btn.getAttribute('data-hero-srcset');
            if (src) heroImg.src = src;
            if (srcset) heroImg.setAttribute('srcset', srcset);
            heroImg.hidden = false;
            if (heroVideo) {
              heroVideo.pause();
              heroVideo.hidden = true;
            }
          }

          thumbs.forEach(function (t) { t.classList.remove('is-active'); });
          btn.classList.add('is-active');

          /* --- Variant-thumb delegation -----------------------------
             Ported from sipfull-product.liquid (2026-07-17). If this
             thumb carries a data-variant-thumb-dim + -value pair, ALSO
             commit the variant change by triggering a click on the
             matching swatch / pill. That lets the customer pick a
             variant by clicking its gallery thumb — matches the non-
             personalised page behaviour. Downstream effects (variant
             ID, price, URL, mug-colour-overlay hero-swap, stock banner)
             are handled by the variant-picker click handler further
             down in this script block. */
          var dim = btn.getAttribute('data-variant-thumb-dim');
          var value = btn.getAttribute('data-variant-thumb-value');
          if (dim && value) {
            /* Special case: clicking a COLOUR thumb while current size
               is Latte or Travel Mug should pivot size back to standard
               ceramic (typically 11oz). Otherwise mug-color-overlay's
               syncFromState sees the special size and forces the hero
               back to the composite, overriding the colour thumb's
               intended ceramic swap. */
            if (dim === 'colour') {
              var sizeContainer = root.querySelector('[data-picker="size"]');
              if (sizeContainer) {
                var activeSize = sizeContainer.querySelector('[data-value].active');
                if (activeSize) {
                  var curSize = (activeSize.getAttribute('data-value') || '').toLowerCase();
                  var isSpecial = curSize.indexOf('latte') >= 0 || curSize.indexOf('travel') >= 0;
                  if (isSpecial) {
                    var sizeBtns = sizeContainer.querySelectorAll('[data-value]');
                    for (var si = 0; si < sizeBtns.length; si++) {
                      var sv = (sizeBtns[si].getAttribute('data-value') || '').toLowerCase();
                      if (sv.indexOf('latte') < 0 && sv.indexOf('travel') < 0) {
                        sizeBtns[si].click();
                        break;
                      }
                    }
                  }
                }
              }
            }

            /* Find and click the matching swatch/pill in the variant picker. */
            var pickerContainer = root.querySelector('[data-picker="' + dim + '"]');
            if (pickerContainer) {
              var swatch = pickerContainer.querySelector('[data-value="' + value.replace(/"/g, '\\"') + '"]');
              if (swatch) swatch.click();
            }

            /* Peek-to-top on mobile (helper defined earlier in this IIFE
               — same one used by the picker-pill click handlers). */
            if (typeof peekToTop === 'function') peekToTop();
          }
        });
      });
    }

    /* --- Quantity stepper ------------------------------------------- */
    if (qtyInput && qtyDec && qtyInc) {
      qtyDec.addEventListener('click', function () {
        var n = parseInt(qtyInput.value, 10) || 1;
        if (n > 1) qtyInput.value = n - 1;
      });
      qtyInc.addEventListener('click', function () {
        var n = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = n + 1;
      });
    }

    /* ----------------------------------------------------------------
       Phase B picker — Size / Material / Colour with Danni's pivot model
       ----------------------------------------------------------------
       Every option stays clickable. If the user clicks a greyed-out
       option that would form an invalid combo, we pivot the OTHER two
       dimensions to the nearest valid variant — ranked by overlap with
       the user's existing selections. That's the behaviour we committed
       to in the mockup and it's what users expect from a modern picker.

       The picker element has three data attributes telling us which
       Shopify option index (1/2/3) is Size vs Material vs Colour, so
       the same JS works regardless of Vela's option ordering.
       ---------------------------------------------------------------- */
    var picker = root.querySelector('.sp-picker-phase-b');
    var idInput = root.querySelector('[data-sp-variant-id]');

    if (picker && variants.length) {
      var sizeIdx     = parseInt(picker.getAttribute('data-option-size'),     10) - 1;
      var materialIdx = parseInt(picker.getAttribute('data-option-material'), 10) - 1;
      var colourIdx   = parseInt(picker.getAttribute('data-option-colour'),   10) - 1;

      /* Shopify's variant JSON gives us either .options (array) or
         .option1/.option2/.option3. Prefer .options since it's clean
         integer indexing — fall back to option{N} strings otherwise. */
      function getDim(v, idx) {
        if (v.options && v.options[idx] != null) return v.options[idx];
        return v['option' + (idx + 1)] || null;
      }

      var state = {
        size:     getDim(variants.find(function (v) { return String(v.id) === idInput.value; }) || variants[0], sizeIdx),
        material: getDim(variants.find(function (v) { return String(v.id) === idInput.value; }) || variants[0], materialIdx),
        colour:   getDim(variants.find(function (v) { return String(v.id) === idInput.value; }) || variants[0], colourIdx)
      };

      /* Find a real variant matching all three dims */
      function findVariant(s) {
        return variants.find(function (v) {
          return getDim(v, sizeIdx)     === s.size &&
                 getDim(v, materialIdx) === s.material &&
                 getDim(v, colourIdx)   === s.colour;
        });
      }

      /* Walk each dim; a value is "disabled" if no variant exists with
         that value + the other two dims held at their current state. */
      function renderAvailability() {
        ['size', 'material', 'colour'].forEach(function (dim) {
          var container = picker.querySelector('[data-picker="' + dim + '"]');
          if (!container) return;
          container.querySelectorAll('[data-value]').forEach(function (el) {
            var candidate = { size: state.size, material: state.material, colour: state.colour };
            candidate[dim] = el.getAttribute('data-value');
            el.classList.toggle('disabled', !findVariant(candidate));
          });
        });

        /* Update each group's selected-label text */
        picker.querySelectorAll('[data-picker]').forEach(function (container) {
          var dim = container.getAttribute('data-picker');
          var group = container.closest('.sp-picker-group');
          var lbl = group && group.querySelector('[data-selected-label]');
          if (lbl) lbl.textContent = state[dim] || '';
        });
      }

      /* Reflect state on active classes across ALL three groups (any
         dim may have pivoted). */
      function renderActive() {
        picker.querySelectorAll('[data-picker]').forEach(function (c) {
          var d = c.getAttribute('data-picker');
          c.querySelectorAll('[data-value]').forEach(function (sib) {
            sib.classList.toggle('active', sib.getAttribute('data-value') === state[d]);
          });
        });
      }

      /* Sync the form's hidden variant id + run the existing price/ATC
         update. Called on every valid state change. */
      function commitState() {
        var v = findVariant(state);
        if (!v) return;
        if (idInput) idInput.value = v.id;
        updateForVariantId(v.id);
      }

      /* Briefly pulse an element so users feel the system reacting.
         Remove + force reflow + re-add so the animation restarts even
         on rapid repeat clicks. */
      function pulse(el) {
        if (!el) return;
        el.classList.remove('pulsed');
        /* eslint-disable-next-line no-void */
        void el.offsetWidth;
        el.classList.add('pulsed');
        setTimeout(function () { el.classList.remove('pulsed'); }, 400);
      }

      /* Wire clicks with pivot logic */
      picker.querySelectorAll('[data-picker]').forEach(function (container) {
        var dim = container.getAttribute('data-picker');
        container.querySelectorAll('[data-value]').forEach(function (el) {
          el.addEventListener('click', function (event) {
            /* Peek-to-top-and-return on DIRECT user taps. Gated on
               event.isTrusted so we don't fire when the thumb handler
               delegates here via a programmatic click (isTrusted:false).
               See peekToTop() helper at the top of this IIFE. */
            if (event && event.isTrusted) {
              peekToTop();
            }

            var newVal = el.getAttribute('data-value');

            /* Snapshot pre-click state so we can detect which OTHER
               dims got pivoted — those pills get a pulse too so users
               see the cascade, not just the thing they tapped. */
            var prev = { size: state.size, material: state.material, colour: state.colour };

            state[dim] = newVal;

            if (!findVariant(state)) {
              /* Pivot — find candidates matching the clicked dim, rank
                 by overlap with the user's previous selections on the
                 OTHER two dims. Higher overlap = we preserve more of
                 what the user already chose, which feels right. */
              var dimIdx = dim === 'size' ? sizeIdx
                         : dim === 'material' ? materialIdx
                         : colourIdx;
              var candidates = variants.filter(function (v) {
                return getDim(v, dimIdx) === newVal;
              });
              if (candidates.length) {
                var best = candidates[0];
                var bestScore = -1;
                candidates.forEach(function (v) {
                  var score = 0;
                  if (getDim(v, sizeIdx)     === state.size)     score++;
                  if (getDim(v, materialIdx) === state.material) score++;
                  if (getDim(v, colourIdx)   === state.colour)   score++;
                  if (score > bestScore) { bestScore = score; best = v; }
                });
                state.size     = getDim(best, sizeIdx);
                state.material = getDim(best, materialIdx);
                state.colour   = getDim(best, colourIdx);
              }
            }

            renderActive();
            renderAvailability();
            commitState();

            /* Always pulse the tapped element — acknowledges the tap. */
            pulse(el);

            /* For any OTHER dim whose value changed as a result of the
               pivot, pulse the newly-active pill/swatch in that group
               so the cascade is visible. */
            ['size', 'material', 'colour'].forEach(function (otherDim) {
              if (otherDim === dim) return;
              if (prev[otherDim] === state[otherDim]) return;
              var otherContainer = picker.querySelector('[data-picker="' + otherDim + '"]');
              if (!otherContainer) return;
              var newActive = otherContainer.querySelector('[data-value="' + state[otherDim] + '"]');
              pulse(newActive);
            });
          });
        });
      });

      /* Honour ?variant= deep-link on load — seed state from that variant */
      var params = new URLSearchParams(window.location.search);
      var deepLinkId = params.get('variant');
      if (deepLinkId) {
        var deepVariant = variants.find(function (v) { return String(v.id) === deepLinkId; });
        if (deepVariant) {
          state.size     = getDim(deepVariant, sizeIdx);
          state.material = getDim(deepVariant, materialIdx);
          state.colour   = getDim(deepVariant, colourIdx);
        }
      }

      renderActive();
      renderAvailability();
      commitState();
    }

    /* ----------------------------------------------------------------
       Phase L picker — single-option "Lid Colour" (travel mugs).
       Much simpler than Phase B: one dimension, no pivoting. Each lid
       swatch maps directly to exactly one variant. Click → find that
       variant by option1 → update id + price + active state + pulse.
       ---------------------------------------------------------------- */
    var lidPicker = root.querySelector('.sp-picker-phase-l');
    if (lidPicker && variants.length) {
      var lidIdInput = root.querySelector('[data-sp-variant-id]');
      var lidLabel   = lidPicker.querySelector('[data-selected-label-lid]');

      function pulseLid(el) {
        if (!el) return;
        el.classList.remove('pulsed');
        /* eslint-disable-next-line no-void */
        void el.offsetWidth;
        el.classList.add('pulsed');
        setTimeout(function () { el.classList.remove('pulsed'); }, 400);
      }

      lidPicker.querySelectorAll('[data-value]').forEach(function (el) {
        el.addEventListener('click', function () {
          var value = el.getAttribute('data-value');
          var variant = variants.find(function (v) {
            if (v.options && v.options[0] != null) return v.options[0] === value;
            return v.option1 === value;
          });
          if (!variant) return;

          if (lidIdInput) lidIdInput.value = variant.id;
          updateForVariantId(variant.id);

          lidPicker.querySelectorAll('[data-value]').forEach(function (s) {
            s.classList.toggle('active', s === el);
          });
          if (lidLabel) lidLabel.textContent = value;

          pulseLid(el);
        });
      });
    }

    /* ----------------------------------------------------------------
       Phase D picker — two colour dimensions (Hubby & Wifey wedding
       mug sets). Mirrors Phase B's pivot model but reduced to two
       dims, so the "score by overlap on other dims" stage only has a
       single other dim to score against — in practice this means:
       if the user-picked combo is unavailable, we hold the OTHER
       side's colour steady if possible, otherwise pick any variant
       matching the just-clicked colour.

       Each swatch group is scoped by [data-picker-dual="a"|"b"];
       selected-label spans by [data-selected-label-dual="a"|"b"].
       ---------------------------------------------------------------- */
    var dualPicker = root.querySelector('.sp-picker-phase-d');
    if (dualPicker && variants.length) {
      var dualIdInput = root.querySelector('[data-sp-variant-id]');

      var aIdx = parseInt(dualPicker.getAttribute('data-option-a'), 10) - 1;
      var bIdx = parseInt(dualPicker.getAttribute('data-option-b'), 10) - 1;

      function getDimD(v, idx) {
        if (v.options && v.options[idx] != null) return v.options[idx];
        return v['option' + (idx + 1)] || null;
      }

      var initialDual = variants.find(function (v) { return String(v.id) === dualIdInput.value; }) || variants[0];
      var dualState = {
        a: getDimD(initialDual, aIdx),
        b: getDimD(initialDual, bIdx)
      };

      function findDualVariant(s) {
        return variants.find(function (v) {
          return getDimD(v, aIdx) === s.a && getDimD(v, bIdx) === s.b;
        });
      }

      function renderDualAvailability() {
        ['a', 'b'].forEach(function (dim) {
          var container = dualPicker.querySelector('[data-picker-dual="' + dim + '"]');
          if (!container) return;
          container.querySelectorAll('[data-value]').forEach(function (el) {
            var candidate = { a: dualState.a, b: dualState.b };
            candidate[dim] = el.getAttribute('data-value');
            el.classList.toggle('disabled', !findDualVariant(candidate));
          });
        });

        /* Update each group's selected-label text */
        ['a', 'b'].forEach(function (dim) {
          var lbl = dualPicker.querySelector('[data-selected-label-dual="' + dim + '"]');
          if (lbl) lbl.textContent = dualState[dim] || '';
        });
      }

      function renderDualActive() {
        ['a', 'b'].forEach(function (dim) {
          var container = dualPicker.querySelector('[data-picker-dual="' + dim + '"]');
          if (!container) return;
          container.querySelectorAll('[data-value]').forEach(function (sib) {
            sib.classList.toggle('active', sib.getAttribute('data-value') === dualState[dim]);
          });
        });
      }

      function commitDualState() {
        var v = findDualVariant(dualState);
        if (!v) return;
        if (dualIdInput) dualIdInput.value = v.id;
        updateForVariantId(v.id);
      }

      function pulseDual(el) {
        if (!el) return;
        el.classList.remove('pulsed');
        /* eslint-disable-next-line no-void */
        void el.offsetWidth;
        el.classList.add('pulsed');
        setTimeout(function () { el.classList.remove('pulsed'); }, 400);
      }

      /* Wire clicks with pivot logic */
      ['a', 'b'].forEach(function (dim) {
        var container = dualPicker.querySelector('[data-picker-dual="' + dim + '"]');
        if (!container) return;
        container.querySelectorAll('[data-value]').forEach(function (el) {
          el.addEventListener('click', function (event) {
            /* Peek-to-top-and-return on DIRECT user taps. Same
               isTrusted gate as the Phase B handler above. */
            if (event && event.isTrusted) {
              peekToTop();
            }

            var newVal = el.getAttribute('data-value');
            var prev = { a: dualState.a, b: dualState.b };

            dualState[dim] = newVal;

            if (!findDualVariant(dualState)) {
              /* Pivot — find candidates matching the clicked dim,
                 prefer ones that preserve the OTHER dim's current
                 value. With only 2 dims, this is binary: either the
                 other dim matches or it doesn't. */
              var dimIdx = dim === 'a' ? aIdx : bIdx;
              var candidates = variants.filter(function (v) {
                return getDimD(v, dimIdx) === newVal;
              });
              if (candidates.length) {
                var best = candidates[0];
                var bestScore = -1;
                candidates.forEach(function (v) {
                  var score = 0;
                  if (getDimD(v, aIdx) === dualState.a) score++;
                  if (getDimD(v, bIdx) === dualState.b) score++;
                  if (score > bestScore) { bestScore = score; best = v; }
                });
                dualState.a = getDimD(best, aIdx);
                dualState.b = getDimD(best, bIdx);
              }
            }

            renderDualActive();
            renderDualAvailability();
            commitDualState();

            pulseDual(el);

            /* Pulse the other dim's new-active swatch if it pivoted */
            ['a', 'b'].forEach(function (otherDim) {
              if (otherDim === dim) return;
              if (prev[otherDim] === dualState[otherDim]) return;
              var otherContainer = dualPicker.querySelector('[data-picker-dual="' + otherDim + '"]');
              if (!otherContainer) return;
              var newActive = otherContainer.querySelector('[data-value="' + dualState[otherDim] + '"]');
              pulseDual(newActive);
            });
          });
        });
      });

      /* Deep-link support */
      var dualParams = new URLSearchParams(window.location.search);
      var dualDeepLinkId = dualParams.get('variant');
      if (dualDeepLinkId) {
        var dualDeepVariant = variants.find(function (v) { return String(v.id) === dualDeepLinkId; });
        if (dualDeepVariant) {
          dualState.a = getDimD(dualDeepVariant, aIdx);
          dualState.b = getDimD(dualDeepVariant, bIdx);
        }
      }

      renderDualActive();
      renderDualAvailability();
      commitDualState();
    }

    /* ---------------------------------------------------------------
       Mobile sticky ATC
       Visibility rules:
         show ←  main ATC has scrolled above the viewport top
                 AND bundle "Add My Set" button not in view
                 AND end-of-section sentinel not in view

       The sticky button triggers atcBtn.click() — reusing the main
       form's submission pipeline means we inherit variant sync, Shop
       Pay / dynamic checkout, and any cart pub/sub downstream.

       The variant label, price and disabled state on the sticky are
       kept in sync with the main ATC via MutationObserver — same price
       block and button text that updateForVariantId() already mutates.
    --------------------------------------------------------------- */
    var stickyEl            = root.querySelector('[data-sp-sticky]');
    var stickyBtn           = root.querySelector('[data-sp-sticky-btn]');
    var stickyBtnText       = root.querySelector('[data-sp-sticky-btn-text]');
    var stickyVariantBtn    = root.querySelector('[data-sp-sticky-variant]');
    var stickyVariantLabel  = root.querySelector('[data-sp-sticky-variant-label]');
    var stickyPriceEl       = root.querySelector('[data-sp-sticky-price]');
    var stickyEndSentinel   = root.querySelector('[data-sp-sticky-end]');

    if (stickyEl && atcBtn) {
      var mainATCScrolledPast = false;
      var bundleInView        = false;
      var endInView           = false;

      function updateStickyVisibility() {
        var show = mainATCScrolledPast && !bundleInView && !endInView;
        stickyEl.classList.toggle('sp-sticky-atc--hidden', !show);
        stickyEl.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      /* Main ATC: rAF-throttled scroll check. IntersectionObserver
         can't distinguish "below fold" (don't show) from "scrolled
         past" (show), so we check the bottom edge manually. */
      var ticking = false;
      function checkMainATC() {
        var rect = atcBtn.getBoundingClientRect();
        var scrolledPast = rect.bottom < 40;
        if (scrolledPast !== mainATCScrolledPast) {
          mainATCScrolledPast = scrolledPast;
          updateStickyVisibility();
        }
        ticking = false;
      }
      function onScrollOrResize() {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(checkMainATC);
        }
      }
      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize, { passive: true });
      checkMainATC();

      /* Bundle ATC — SellEasy renders async, so poll for up to 10s. */
      function watchBundle() {
        var bundleBtn = document.querySelector('.lb-button.lb-button-bundle');
        if (!bundleBtn) return false;
        var obs = new IntersectionObserver(function (entries) {
          bundleInView = entries[0].isIntersecting;
          updateStickyVisibility();
        }, { threshold: 0.15 });
        obs.observe(bundleBtn);
        return true;
      }
      if (!watchBundle()) {
        var bundleTries = 0;
        var bundleTimer = setInterval(function () {
          bundleTries++;
          if (watchBundle() || bundleTries > 20) clearInterval(bundleTimer);
        }, 500);
      }

      /* End-of-section sentinel */
      if (stickyEndSentinel) {
        var endObs = new IntersectionObserver(function (entries) {
          endInView = entries[0].isIntersecting;
          updateStickyVisibility();
        }, { threshold: 0 });
        endObs.observe(stickyEndSentinel);
      }

      /* Tap sticky button → trigger the main ATC. Preserves whatever
         form context it holds so Shopify's payment_button keeps working. */
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function (e) {
          if (stickyBtn.hasAttribute('disabled')) return;
          e.preventDefault();
          atcBtn.click();
        });
      }

      /* Tap variant chip → scroll the info column into view and focus
         the picker so users can change their selection without hunting. */
      if (stickyVariantBtn) {
        stickyVariantBtn.addEventListener('click', function () {
          var info   = root.querySelector('.sp-info');
          var picker = root.querySelector('.sp-picker-phase-b')
                    || root.querySelector('.sp-variant-select');
          if (info) info.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (picker && picker.focus) {
            /* Delay focus so the smooth-scroll settles first. */
            setTimeout(function () {
              picker.focus({ preventScroll: true });
            }, 300);
          }
        });
      }

      /* Current variant id — idInput is only present in Phase B,
         select in Phase A. Exactly one exists at a time. */
      function currentVariantId() {
        if (idInput) return idInput.value;
        if (select)  return select.value;
        return null;
      }

      /* Mirror price, button text + disabled state, and variant label
         from the main form into the sticky. Safe to call any time. */
      function syncStickyFromMain() {
        if (stickyPriceEl && priceWrap) {
          var sale    = priceWrap.querySelector('.sp-price-sale');
          var compare = priceWrap.querySelector('.sp-price-compare');
          var regular = priceWrap.querySelector('.sp-price-regular');
          if (sale) {
            stickyPriceEl.innerHTML =
              '<span class="sp-sticky-price-sale">' + sale.textContent.trim() + '</span>' +
              (compare
                ? '<span class="sp-sticky-price-compare">' + compare.textContent.trim() + '</span>'
                : '');
          } else if (regular) {
            stickyPriceEl.innerHTML =
              '<span class="sp-sticky-price-regular">' + regular.textContent.trim() + '</span>';
          }
        }

        if (stickyBtn && stickyBtnText && atcBtn && atcTxt) {
          stickyBtnText.textContent = atcTxt.textContent.trim();
          if (atcBtn.hasAttribute('disabled')) {
            stickyBtn.setAttribute('disabled', 'disabled');
          } else {
            stickyBtn.removeAttribute('disabled');
          }
        }

        if (stickyVariantLabel) {
          var vid = currentVariantId();
          if (vid) {
            var v = variants.find(function (x) { return String(x.id) === String(vid); });
            if (v && v.title && v.title !== 'Default Title') {
              /* Truncate long variant titles client-side to match Liquid's truncate */
              var t = v.title.length > 22 ? v.title.slice(0, 21).trim() + '…' : v.title;
              stickyVariantLabel.textContent = t;
            }
          }
        }
      }

      /* MutationObserver on the main price block + ATC catches every
         variant update — both Phase A (select change) and Phase B
         (picker → commitState → updateForVariantId). Saves us from
         bolting bespoke callbacks into each code path. */
      if (window.MutationObserver) {
        if (priceWrap) {
          new MutationObserver(syncStickyFromMain)
            .observe(priceWrap, { childList: true, subtree: true, characterData: true });
        }
        if (atcTxt) {
          new MutationObserver(syncStickyFromMain)
            .observe(atcTxt, { childList: true, characterData: true });
        }
        if (atcBtn) {
          new MutationObserver(syncStickyFromMain)
            .observe(atcBtn, { attributes: true, attributeFilter: ['disabled'] });
        }
      }

      syncStickyFromMain();
    }

    /* ---------------------------------------------------------------
       Open cart drawer on any /cart/add call

       The main ATC now goes through the theme's <product-form>, which
       opens the drawer natively after a successful add (settings-driven,
       currently set to "drawer"). But SellEasy's add-ons widget and the
       bundle's "Add My Set" button POST to /cart/add.js on their own —
       they never hit the theme's product-form code path.

       Hook window.fetch and XMLHttpRequest once per page to detect any
       POST to /cart/add or /cart/add.js, and on success dispatch the
       drawer's refresh + open events (see assets/cart-drawer.js — the
       drawer listens for `dispatch:cart-drawer:refresh` and
       `dispatch:cart-drawer:open`).

       __sipfullCartDrawerHooked guard ensures we don't double-wrap if
       the section is re-initialized by Shopify's theme editor.
    --------------------------------------------------------------- */
    if (!window.__sipfullCartDrawerHooked) {
      window.__sipfullCartDrawerHooked = true;

      var openCartDrawer = function () {
        /* Slight delay so the third-party widget has time to finish its
           own post-add UI updates before the drawer takes over. */
        setTimeout(function () {
          try {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
          } catch (e) { /* noop */ }
        }, 150);
      };

      var isCartAddUrl = function (url) {
        if (!url) return false;
        try {
          return /\/cart\/add(\.js)?(\?|$|#)/.test(String(url));
        } catch (e) { return false; }
      };

      /* fetch patch — catches SellEasy and modern apps */
      if (window.fetch) {
        var nativeFetch = window.fetch.bind(window);
        window.fetch = function (input, init) {
          var url = typeof input === 'string' ? input : (input && input.url);
          var method = (init && init.method)
                     || (input && input.method)
                     || 'GET';
          var isAdd = isCartAddUrl(url) && /post/i.test(method);
          var p = nativeFetch(input, init);
          if (isAdd) {
            p.then(function (res) {
              if (res && res.ok) openCartDrawer();
            }).catch(function () { /* noop */ });
          }
          return p;
        };
      }

      /* XHR patch — catches older widgets that use XMLHttpRequest */
      if (window.XMLHttpRequest) {
        var origOpen = window.XMLHttpRequest.prototype.open;
        var origSend = window.XMLHttpRequest.prototype.send;
        window.XMLHttpRequest.prototype.open = function (method, url) {
          this.__sipfullIsAdd = isCartAddUrl(url) && /post/i.test(method);
          return origOpen.apply(this, arguments);
        };
        window.XMLHttpRequest.prototype.send = function () {
          var xhr = this;
          if (xhr.__sipfullIsAdd) {
            xhr.addEventListener('load', function () {
              if (xhr.status >= 200 && xhr.status < 300) openCartDrawer();
            });
          }
          return origSend.apply(this, arguments);
        };
      }
    }
  })();
